import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, Coins, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { fetchLeaderboard, type LeaderRow } from '@/lib/api';

export default function Leaderboard() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LeaderRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchLeaderboard(50)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="section">
      <div className="container-narrow max-w-3xl">
        <div className="text-center mb-12">
          <h1 className="font-pixel text-5xl font-bold tracking-tight">{t('leaderboard_page.title')}</h1>
          <p className="mt-4 text-kquest-300">{t('leaderboard_page.subtitle')}</p>
        </div>

        {loading ? (
          <div className="text-center text-kquest-300 font-pixel py-16">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyLeaderboard />
        ) : (
          <div className="kblock p-6 sm:p-8">
            <div className="grid grid-cols-12 text-[10px] font-pixel uppercase tracking-wider text-kquest-400 pb-3 border-b-2 border-kquest-700">
              <div className="col-span-1">#</div>
              <div className="col-span-5">{t('leaderboard_page.player')}</div>
              <div className="col-span-2 text-right">{t('leaderboard_page.votes')}</div>
              <div className="col-span-2 text-right">{t('leaderboard_page.won')}</div>
              <div className="col-span-2 text-right">{t('leaderboard_page.spent')}</div>
            </div>
            {rows.map((u, i) => {
              const rank = i + 1;
              return (
                <motion.div
                  key={u.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.04 }}
                  className="grid grid-cols-12 items-center py-3 border-b border-kquest-700/40 font-pixel text-kquest-200"
                >
                  <div className={`col-span-1 font-press text-base ${
                    rank === 1 ? 'text-gold-400' :
                    rank === 2 ? 'text-kquest-200' :
                    rank === 3 ? 'text-redstone-400' :
                    'text-kquest-500'
                  }`}>
                    {rank}
                  </div>
                  <div className="col-span-5 truncate flex items-center gap-2">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-sm border border-kquest-500" />
                    ) : null}
                    <span className="truncate">{u.username}</span>
                    {u.tier && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded
                                       bg-violet-500/20 border border-violet-500/40 text-violet-200">
                        {u.tier}
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 text-right">{u.votes}</div>
                  <div className="col-span-2 text-right text-gold-400 flex items-center justify-end gap-1">
                    {u.won_quests > 0 && <Trophy className="w-3 h-3" />} {u.won_quests}
                  </div>
                  <div className="col-span-2 text-right text-xp-400 flex items-center justify-end gap-1">
                    <Coins className="w-3 h-3" /> €{(u.spent_cents/100).toFixed(2)}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function EmptyLeaderboard() {
  const { t } = useTranslation();
  return (
    <div className="kblock p-12 text-center">
      <BarChart3 className="w-16 h-16 text-violet-400 mx-auto mb-6 opacity-80" />
      <h3 className="font-pixel text-2xl mb-3">
        {t('leaderboard_page.empty_title', { defaultValue: 'Classifica ancora vuota' })}
      </h3>
      <p className="text-kquest-300 mb-6">
        {t('leaderboard_page.empty_body', {
          defaultValue: 'Vota la tua quest preferita per comparire qui.',
        })}
      </p>
      <Link to="/quests" className="btn-purple !px-6 !py-3">
        {t('leaderboard_page.empty_cta', { defaultValue: 'Vai alle quest attive' })}
      </Link>
    </div>
  );
}
